import {
  Disclosure,
  DisclosureButton as _DisclosureButton,
  DisclosurePanel as _DisclosurePanel,
} from "@reach/disclosure"
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"
import React, { useState } from "react"
import styled, { css } from "styled-components"
import Breakpoints from "../../components/shared/breakpoints"
import Arrow from "../../components/shared/svg/rounded-arrow"
import Text, { T } from "../atoms/text"
import ContentBlock from "../molecules/content-block"

const FAQItemWrapper = styled.div`
  :not(:first-of-type) {
    padding-top: 24px;
    @media (min-width: ${Breakpoints.lg}) {
      padding-top: 32px;
    }
  }
  :not(:last-of-type) {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 24px;
    @media (min-width: ${Breakpoints.lg}) {
      padding-bottom: 32px;
    }
  }
`

const DisclosureButton = styled(_DisclosureButton)<{ $open: boolean }>`
  all: unset;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 24px;
  align-items: center;
  cursor: pointer;
  & svg {
    transition: transform ease-in 0.2s;
    // $open is a transient prop https://styled-components.com/docs/api#transient-props
    transform: rotate(${(props) => (props.$open ? "90deg" : "0deg")});
  }
`
const DisclosurePanel = styled(_DisclosurePanel)`
  ul {
    list-style-type: disc;
    margin-top: 1rem;
    padding-left: 1.5rem;
    line-height: 200%;
  }
  span {
    display: block;
    :not(:first-of-type) {
      margin-top: 1.5rem;
    }
  }
`

export interface IFAQItemProps {
  title: React.ReactNode
  text: React.ReactNode
}

const FAQItem = (props: IFAQItemProps) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <FAQItemWrapper>
      <Disclosure open={isOpen} onChange={() => setIsOpen((x) => !x)}>
        <DisclosureButton $open={isOpen}>
          <Text
            as="h3"
            variants={{
              sm: css`
                ${T.sm.h4`500`}
                && {
                  text-transform: unset;
                  color: var(--slate);
                  letter-spacing: unset;
                  display: inline-block;
                  padding-right: 48px;
                }
              `,
              md: T.md.h4`500`,
              lg: T.lg.h4`500`,
            }}
          >
            {props.title}
          </Text>
          <Arrow color="var(--burnt-orange)" />
        </DisclosureButton>
        <DisclosurePanel>
          <Text.Body
            variants={{
              sm: css`
                margin-top: 16px;
              `,
              md: { marginTop: "24px" },
            }}
          >
            {props.text}
          </Text.Body>
        </DisclosurePanel>
      </Disclosure>
    </FAQItemWrapper>
  )
}

export interface IRichTextFAQItemProps {
  title: string
  body: RenderRichTextData<ContentfulRichTextGatsbyReference>
}

FAQItem.RichText = ({ title, body }: IRichTextFAQItemProps) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <FAQItemWrapper>
      <Disclosure open={isOpen} onChange={() => setIsOpen((x) => !x)}>
        <DisclosureButton $open={isOpen}>
          <Text
            as="h3"
            variants={{
              sm: css`
                ${T.sm.h4`500`}
                && {
                  text-transform: unset;
                  color: var(--slate);
                  letter-spacing: unset;
                  display: inline-block;
                  padding-right: 48px;
                }
              `,
              md: T.md.h4`500`,
              lg: T.lg.h4`500`,
            }}
          >
            {title}
          </Text>
          <Arrow color="var(--burnt-orange)" />
        </DisclosureButton>
        <DisclosurePanel>
          <ContentBlock.RichText
            variants={{
              sm: css`
                margin-top: 16px;
              `,
              md: { marginTop: "24px" },
            }}
          >
            {body}
          </ContentBlock.RichText>
        </DisclosurePanel>
      </Disclosure>
    </FAQItemWrapper>
  )
}

export default FAQItem
